import React, { type ComponentProps, useCallback } from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import { IconButton } from '@adsk/alloy-react-button';
import { useControlled, useForwardedRef } from '@adsk/alloy-react-helpers';
import { MagnifyingGlassIcon, XCircleFilledIcon } from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import TextInput, {
  type RenderElementType,
} from '@adsk/alloy-react-text-input';
import theme, { stylePropType } from '@adsk/alloy-react-theme';

import useKeyDown from './hooks/useKeyDown';

type SearchFieldProps = Omit<ComponentProps<typeof TextInput>, 'onChange'> & {
  loading?: boolean;
  inlineText?: string;
  onClear?: () => void;
  onSearch?: () => void;
  onChange?: (value: string) => void;
};

const StyledTextInput: typeof TextInput = styledC(TextInput).attrs({
  role: 'searchbox',
  ['data-testid']: 'SearchField__input',
})`
  & input[type="search"] {
    padding-bottom: 0px;
    ${theme.typography.ellipsis};
  }
  // Remove default browser clear button
  & input[type="search"]::-webkit-search-decoration, input[type="search"]::-webkit-search-cancel-button, input[type="search"]::-webkit-search-results-button, input[type="search"]::-webkit-search-results-decoration {
    display: none;
  }
`;

const StyledMagnifyingGlassButton = styledC(IconButton).attrs({
  renderIcon: MagnifyingGlassIcon.renderIcon,
})`
  color: ${theme.tokens.colors.icon.tertiary.value};
  ${(props) =>
    !props.disabled && {
      pointerEvents: 'auto',
      cursor: 'pointer',
    }}
  `;

const StyledDiv = styledC.div`
  height: 36px;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;
  gap: ${theme.spacing.XS}px;
`;

const StyledSpan = styledC.span`
  ${theme.typography.bodySmall};
  color: ${theme.tokens.colors.text.primary.value};
  pointer-events: none;
`;

/**
 * A SearchField component with a magnifying glass and a clear input text button
 */
const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(
  (
    {
      disabled = false,
      loading = false,
      type = 'search',
      defaultValue,
      inlineText = '',
      onClear,
      onSearch,
      value: valueProp,
      onChange: onChangeProp,
      ...props
    },
    ref,
  ) => {
    const inputRef = useForwardedRef<HTMLInputElement>(ref);
    const [value, onChange] = useControlled(
      defaultValue?.toString() ?? '',
      valueProp,
      onChangeProp,
    );

    const clearInput = useCallback(() => {
      onChange?.('');
      onClear?.();
    }, [onChange, onClear]);

    useKeyDown(inputRef.current, 'Escape', clearInput);
    useKeyDown(inputRef.current, 'Enter', () => onSearch?.());
    useKeyDown(inputRef.current, 'NumpadEnter', () => onSearch?.());

    const renderLeftElement: RenderElementType = useCallback(
      (magnifyingGlassProps) => (
        <StyledMagnifyingGlassButton
          {...magnifyingGlassProps}
          disabled={disabled}
          onClick={() => {
            inputRef.current?.focus();
            onSearch?.();
          }}
        />
      ),
      [disabled, inputRef, onSearch],
    );

    const renderRightElement: RenderElementType = useCallback(
      ({ size: iconSize, ...rightContainerProps }) => (
        <StyledDiv
          {...rightContainerProps}
          style={{ ...rightContainerProps.style, pointerEvents: 'auto' }}
        >
          {inlineText && <StyledSpan>{inlineText}</StyledSpan>}
          {loading && (
            <ProgressRing
              data-testid="progress-ring"
              size={ProgressRing.SIZES.XSMALL}
            />
          )}
          {value && !disabled && (
            <IconButton
              data-testid="SearchField__ClearButton"
              tabIndex={-1}
              size={iconSize}
              renderIcon={XCircleFilledIcon.renderIcon}
              onClick={(e) => {
                e.preventDefault();
                clearInput();
              }}
            />
          )}
        </StyledDiv>
      ),
      [clearInput, disabled, inlineText, loading, value],
    );

    return (
      <StyledTextInput
        value={value}
        type={type}
        {...props}
        ref={inputRef}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        renderLeftElement={renderLeftElement}
        renderRightElement={
          value || inlineText || loading ? renderRightElement : undefined
        }
      />
    );
  },
);

SearchField.displayName = 'SearchField';

SearchField.propTypes = {
  /* Styles applied to the input element */
  style: stylePropType,
  /* Class applied to the input element */
  className: PropTypes.string,
  /* Toggle disabled state */
  disabled: PropTypes.bool,
  /* Toggle loading spinner */
  loading: PropTypes.bool,
  /* initial default text value */
  defaultValue: PropTypes.string,
  /* controlled input value */
  value: PropTypes.string,
  /* Child input's onChange handler */
  onChange: PropTypes.func,
  /* Any text to display at the right of the input,
   * intended to show the number of results returned from the search
   **/
  inlineText: PropTypes.string,
};
export default SearchField;
